import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Share2 } from 'lucide-react';
import {
  effectiveDefaultSessionShare,
  sessionSharingPolicy,
  sessionSharingSettingCovered,
  sessionSharingSettingStored,
  sessionSharingSettingSummary,
  storedDefaultSessionShare,
  type DefaultSessionShare,
  type Organization,
  type SessionSharingPolicy,
  type SessionSharingSetting,
  type SessionSharingSettings,
} from '@reflex/shared';
import { cn } from '@reflex/ui/lib/utils';
import { Label } from '@reflex/ui/components/ui/label';
import { Separator } from '@reflex/ui/components/ui/separator';
import { Switch } from '@reflex/ui/components/ui/switch';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { DirtyActionBar } from '@reflex/ui/components/shared/DirtyActionBar';
import { RequireActiveOrganization } from '@/components/shared/RequireActiveOrganization';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { usePlugins } from '@/hooks/config/usePlugins';
import { api } from '@/lib/api';
import { mutateWithToast } from '@/lib/mutation-helpers';

/**
 * Who may hand out access to a session, org-wide. Two switches, because the
 * two levels answer different questions: whether a session may be read by
 * someone it was not launched by, and whether anyone but the owner may write
 * to it. Turning edit sharing off is a separation-of-duties control, so the
 * server refuses write shares for every caller (org admins included) rather
 * than trusting the share dialog to grey the option out. With both off,
 * sessions are not shareable and the share button disappears.
 *
 * Narrowing the policy leaves existing shares alone: it stops the next share,
 * it does not silently strip access someone is working with.
 */
function SharingCard({
  org,
  canManage,
  settings,
}: {
  org: Organization;
  canManage: boolean;
  /** The sharing settings installed plugins register, in display order. */
  settings: SessionSharingSetting[];
}) {
  const { refreshSession } = useAuth();
  const saved = sessionSharingPolicy(org);
  const savedSettings = Object.fromEntries(
    settings.map((setting) => [setting.key, sessionSharingSettingStored(setting, org)]),
  );
  const savedDefaultShare = storedDefaultSessionShare(org);
  const [allowView, setAllowView] = useState(saved.view);
  const [allowEdit, setAllowEdit] = useState(saved.edit);
  const [defaultShare, setDefaultShare] = useState<DefaultSessionShare>(savedDefaultShare);
  // Starts empty rather than seeded from `savedSettings`: the plugin
  // manifests `settings` comes from are a separate, async fetch (unlike
  // `org`, already resolved before this component mounts), so a one-shot
  // snapshot taken before they load would never re-sync. An untouched key
  // simply has no entry here and falls back to the live `savedSettings`
  // read below, which is safe to recompute every render.
  const [settingValues, setSettingValues] = useState<SessionSharingSettings>({});

  const updateMutation = useMutation({
    mutationFn: (data: {
      allowSessionShareView?: boolean;
      allowSessionShareEdit?: boolean;
      defaultSessionShare?: DefaultSessionShare;
      sharingSettings?: SessionSharingSettings;
    }) => api.updateOrganization(org.id, data),
    onSuccess: async () => {
      // The policy rides the org object in the `useAuth` reducer (fed by
      // /auth/me), not a React Query cache, so the share affordances read a
      // stale policy until the session is re-fetched.
      await refreshSession();
    },
  });

  // The ceiling being edited, not the one that is saved: a plugin setting
  // greys out the moment its level is switched off, before Save.
  const pending: SessionSharingPolicy = { view: allowView, edit: allowEdit };
  // What the pending ceiling would actually let the pending default do. The
  // server clamps the same way, so a default the ceiling forbids is shown as
  // Private here rather than as a promise the next launch would not keep.
  const pendingDefault = effectiveDefaultSessionShare({
    allowSessionShareView: allowView,
    allowSessionShareEdit: allowEdit,
    defaultSessionShare: defaultShare,
  });
  const changedSettings = Object.fromEntries(
    Object.entries(settingValues).filter(([key, value]) => value !== savedSettings[key]),
  );
  const dirty =
    allowView !== saved.view ||
    allowEdit !== saved.edit ||
    defaultShare !== savedDefaultShare ||
    Object.keys(changedSettings).length > 0;

  function handleReset() {
    setAllowView(saved.view);
    setAllowEdit(saved.edit);
    setDefaultShare(savedDefaultShare);
    setSettingValues({});
  }

  function handleSave() {
    if (!dirty) return;
    const updates: {
      allowSessionShareView?: boolean;
      allowSessionShareEdit?: boolean;
      defaultSessionShare?: DefaultSessionShare;
      sharingSettings?: SessionSharingSettings;
    } = {};
    if (allowView !== saved.view) updates.allowSessionShareView = allowView;
    if (allowEdit !== saved.edit) updates.allowSessionShareEdit = allowEdit;
    // The chosen value, not the clamped one: the org's recorded intent
    // survives a ceiling that is turned off and back on again.
    if (defaultShare !== savedDefaultShare) updates.defaultSessionShare = defaultShare;
    // Only the keys that changed: the server merges the patch, so an
    // untouched setting keeps whatever it already had.
    if (Object.keys(changedSettings).length > 0) updates.sharingSettings = changedSettings;
    mutateWithToast(updateMutation, updates, {
      success: 'Sharing settings updated',
      error: 'Failed to update sharing settings',
    });
  }

  return (
    <SectionCard
      title="Sharing"
      description="Control how members can share their sessions with other people and teams."
      icon={<Share2 />}
      data-testid="org-sharing-section"
    >
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <SectionLabel as="h3" tone="strong">
              Session sharing
            </SectionLabel>
            <p className="text-xs text-muted-foreground">
              Applies to the share button on every session, including shares picked at launch and
              access granted by approving a request.
            </p>
          </div>
          <div className="flex items-center justify-between gap-4 max-w-md">
            <Label htmlFor="org-share-view">Allow sharing with view access</Label>
            <Switch
              id="org-share-view"
              checked={allowView}
              disabled={!canManage}
              onCheckedChange={setAllowView}
              aria-label="Allow sharing with view access"
            />
          </div>
          <div className="flex items-center justify-between gap-4 max-w-md">
            <Label htmlFor="org-share-edit">Allow sharing with edit access</Label>
            <Switch
              id="org-share-edit"
              checked={allowEdit}
              disabled={!canManage}
              onCheckedChange={setAllowEdit}
              aria-label="Allow sharing with edit access"
            />
          </div>
          <p className="text-xs text-muted-foreground" data-testid="org-sharing-summary">
            {!allowView && !allowEdit
              ? 'Sessions cannot be shared. The share button is hidden and no new access can be granted.'
              : !allowEdit
                ? 'Sessions can be shared read-only. Nobody can be given write access to a session they do not own.'
                : !allowView
                  ? 'Sessions can only be shared with edit access. View-only sharing is turned off.'
                  : 'Members can share a session at either level with people, teams, or the whole organization.'}
          </p>
          <p className="text-xs text-muted-foreground">
            Turning a level off does not revoke shares that are already open.
          </p>
        </section>

        <DefaultShareSection
          value={defaultShare}
          effective={pendingDefault}
          policy={pending}
          canManage={canManage}
          onChange={setDefaultShare}
        />

        {settings.map((setting) => (
          <PluginSharingSection
            key={setting.key}
            setting={setting}
            policy={pending}
            stored={settingValues[setting.key] ?? savedSettings[setting.key]}
            defaultShare={pendingDefault}
            canManage={canManage}
            onChange={(value) =>
              setSettingValues((current) => ({ ...current, [setting.key]: value }))
            }
          />
        ))}

        {canManage && (
          <>
            <Separator />
            <DirtyActionBar
              dirty={dirty}
              saving={updateMutation.isPending}
              onDiscard={handleReset}
              onSave={handleSave}
              permission="org:manage"
            />
          </>
        )}
      </div>
    </SectionCard>
  );
}

/** The three defaults, in the order the page offers them. */
const DEFAULT_SHARE_OPTIONS: {
  value: DefaultSessionShare;
  label: string;
  description: string;
}[] = [
  {
    value: 'private',
    label: 'Private',
    description:
      'A new session is visible only to whoever launched it, until they share it themselves.',
  },
  {
    value: 'view',
    label: 'Everyone can view',
    description: 'Everyone in the organization can open and read new sessions.',
  },
  {
    value: 'edit',
    label: 'Everyone can edit',
    description: 'Everyone in the organization can open new sessions and send them work.',
  },
];

/**
 * What new sessions start as, org-wide.
 *
 * This is a default, not a ceiling, and the difference is the whole point of
 * keeping it in its own section: the switches above say what a member is
 * *allowed* to share, this says what happens when nobody chooses. It is
 * applied when the session is created, as an ordinary org-wide share on that
 * one session, so it is visible and revocable like any other share and
 * changing it later leaves every session that already exists exactly as it
 * is.
 *
 * A level the ceiling forbids is offered but not selectable, and a stored
 * default the ceiling has since forbidden reads as Private rather than
 * quietly disappearing: the org's choice is still recorded, and turning the
 * level back on restores it.
 */
function DefaultShareSection({
  value,
  effective,
  policy,
  canManage,
  onChange,
}: {
  /** The org's recorded choice, which may exceed what `policy` allows. */
  value: DefaultSessionShare;
  /** What that choice resolves to under `policy`, and what will happen. */
  effective: DefaultSessionShare;
  policy: SessionSharingPolicy;
  canManage: boolean;
  onChange: (next: DefaultSessionShare) => void;
}) {
  const allowed = (option: DefaultSessionShare) => option === 'private' || policy[option];
  const clamped = value !== effective;
  return (
    <>
      <Separator />
      <section className="flex flex-col gap-3" data-testid="org-default-share">
        <div className="flex flex-col gap-1">
          <SectionLabel as="h3" tone="strong">
            Default sharing for new sessions
          </SectionLabel>
          <p className="text-xs text-muted-foreground">
            Applies to every new session, however it was started: the launch dialog, Slack, the CLI,
            or the API. Whoever launches a session can still change the sharing on it afterwards.
          </p>
        </div>
        {/* A div, not <ul>: the implicit `listitem` role of list children is
            invalid inside a radiogroup (axe `listitem`). */}
        <div
          role="radiogroup"
          aria-label="Default sharing for new sessions"
          className="flex max-w-md flex-col gap-2"
        >
          {DEFAULT_SHARE_OPTIONS.map((option) => {
            const selectable = canManage && allowed(option.value);
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={!selectable}
                onClick={() => onChange(option.value)}
                data-testid={`org-default-share-${option.value}`}
                data-selected={selected || undefined}
                className={cn(
                  'flex w-full flex-col gap-0.5 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  selected
                    ? 'border-foreground/40 bg-muted/30'
                    : 'border-border/40 bg-transparent hover:border-border hover:bg-muted/20',
                  !selectable &&
                    'cursor-not-allowed opacity-60 hover:border-border/40 hover:bg-transparent',
                )}
              >
                <span className="text-sm font-medium">{option.label}</span>
                <span className="text-xs text-muted-foreground">{option.description}</span>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground" data-testid="org-default-share-summary">
          {clamped
            ? `This organization no longer allows sharing sessions with ${value} access, so new sessions start private until that level is turned back on.`
            : effective === 'private'
              ? 'New sessions start private. Nobody else can open one until it is shared.'
              : `New sessions are shared with the whole organization at ${effective} access as soon as they are created.`}
        </p>
        <p className="text-xs text-muted-foreground">
          Changing this only affects sessions created from now on. Sessions that already exist keep
          the sharing they have.
        </p>
      </section>
    </>
  );
}

/**
 * One switch for a sharing setting a plugin registered — the same layout
 * for every plugin, because the rule they all obey is the host's: a setting
 * can never hand out more access than the two levels above it, so it reads
 * off and unusable while its level is off, whatever the org stored.
 *
 * Everything the section says comes from the declaration
 * (`SessionSharingSetting` in `@reflex/shared`), so a new plugin adds a
 * section here without touching this page.
 */
function PluginSharingSection({
  setting,
  policy,
  stored,
  defaultShare,
  canManage,
  onChange,
}: {
  setting: SessionSharingSetting;
  policy: SessionSharingPolicy;
  stored: boolean;
  /**
   * What new sessions start as org-wide. When it already gives everyone
   * what this setting hands out, the setting is moot for new sessions —
   * but only for new ones. The default is not retroactive, so a session
   * created before it was set has no org-wide share and still needs this
   * setting to open one, and the server keeps honouring it.
   */
  defaultShare: DefaultSessionShare;
  canManage: boolean;
  onChange: (value: boolean) => void;
}) {
  const available = policy[setting.grants];
  const covered = sessionSharingSettingCovered(setting, defaultShare);
  const switchId = `org-sharing-setting-${setting.key}`;
  return (
    <>
      <Separator />
      <section className="flex flex-col gap-3" data-testid={`org-sharing-setting-${setting.key}`}>
        <div className="flex flex-col gap-1">
          <SectionLabel as="h3" tone="strong">
            {setting.copy.title}
          </SectionLabel>
          <p className="text-xs text-muted-foreground">{setting.copy.description}</p>
        </div>
        <div className="flex items-center justify-between gap-4 max-w-md">
          <Label htmlFor={switchId}>{setting.copy.switchLabel}</Label>
          <Switch
            id={switchId}
            checked={stored && available}
            // A covered setting that is already OFF stays off: turning it on
            // would change nothing for new sessions, which is what "covered"
            // means. A covered setting that is ON must stay switchable —
            // the default is not retroactive, so this switch is still the
            // only control over sessions created before the default was
            // set, and disabling it outright would strand them.
            disabled={!canManage || !available || (covered && !stored)}
            onCheckedChange={onChange}
            aria-label={setting.copy.switchLabel}
          />
        </div>
        <p
          className="text-xs text-muted-foreground"
          data-testid={`org-sharing-setting-summary-${setting.key}`}
        >
          {sessionSharingSettingSummary(setting, { stored, available, defaultShare })}
        </p>
        {setting.copy.disclosure && (
          <p className="text-xs text-muted-foreground">{setting.copy.disclosure}</p>
        )}
      </section>
    </>
  );
}

/**
 * Org-wide sharing policy page (Configure → Usage and Costs → Sharing).
 * Everyone with the nav row can read the current policy; edits are gated on
 * `org:manage` like the rest of the org settings surface.
 */
export function SharingSettingsPage() {
  return (
    <RequireActiveOrganization title="Sharing">
      {(org) => <SharingSettingsBody key={org.id} />}
    </RequireActiveOrganization>
  );
}

function SharingSettingsBody() {
  const { activeOrganization, can } = useOrganization();
  const { sessionSharingSettings } = usePlugins();
  if (!activeOrganization) return null;
  return (
    <PageLayout maxWidth="full">
      <div className="mx-auto flex w-full max-w-6xl">
        <div className="min-w-0 flex-1">
          <PageHeader
            iconVariant="box"
            icon={<Share2 className="h-5 w-5 text-muted-foreground" />}
            title="Sharing"
            description="Organization-wide limits on how sessions can be shared."
          />
          <div className="mt-6 space-y-8">
            <SharingCard
              org={activeOrganization}
              canManage={can('org:manage')}
              settings={sessionSharingSettings}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
